import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// API Base URL configuration
const getApiBaseUrl = (): string => {
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:4000';
    }
  }

  return 'https://api.acconto.ai';
};

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: getApiBaseUrl() + '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// API request configuration interface
export interface ApiRequestConfig {
  endpoint: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  token?: string;
}

// Error response interface
export interface ApiError {
  message: string;
  status: number;
  details?: unknown;
}

// Main API request function
export const apiRequest = async <T>(config: ApiRequestConfig): Promise<T> => {
  try {
    const requestConfig: AxiosRequestConfig = {
      method: config.method,
      url: config.endpoint,
      data: config.body,
      headers: {
        ...config.headers,
        ...(config.token && { Authorization: `Bearer ${config.token}` }),
      },
    };

    const response = await apiClient.request<T>(requestConfig);
    return response.data;
  } catch (error) {
    console.error('Error in axios request', error);
    if (axios.isAxiosError(error)) {
      const apiError: ApiError = {
        message:
          error.response?.data?.message || error.message || 'Request failed',
        status: error.response?.status || 500,
        details: error.response?.data,
      };
      throw apiError;
    }
    throw new Error('An unexpected error occurred');
  }
};

export const API_BASE_URL = getApiBaseUrl();
